import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import { useStore } from 'core/store';
import { PaymentResultStatus as Status } from './payment-result';
import useStyles from './payment-result-status.styles';

const icons = {
  [Status.Succeeded]: CheckCircleOutlineIcon,
  [Status.Pending]: AccessTimeIcon,
  [Status.Failed]: ErrorOutlineIcon,
  [Status.Unconfirmed]: ErrorOutlineIcon
};

const isProblem = (result) => [Status.Failed, Status.Unconfirmed].includes(result?.status);

/**
 * The latest manual-charge or refund outcome for this policy, shown under the form that produced it
 * (#36632) until staff leave the tab. Both live regions stay mounted and empty, and the result is
 * written into one a frame later, so screen readers announce it even when the form has just remounted
 * after the post-charge refresh: successes and pending results politely, problems as alerts.
 */
const PaymentResultStatus = observer(function PaymentResultStatus({ form }) {
  const classes = useStyles();
  const { policy, paymentResults } = useStore().account.policies.policy;
  const stored = paymentResults[form];
  const result = stored && stored.policyId === policy?.id ? stored : null;

  const [shown, setShown] = useState(null);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(result));
    return () => cancelAnimationFrame(frame);
  }, [result]);

  const renderResult = () => {
    const Icon = icons[shown.status];
    return (
      <div
        key={shown.submittedAt}
        data-status={shown.status}
        className={classNames(
          classes.result,
          isProblem(shown) ? classes.failed : [classes[shown.status], classes.enter]
        )}
      >
        <Icon className={classes.icon} aria-hidden="true" />
        <div>
          <strong className={classes.title}>{shown.title}</strong>
          <span className={classes.detail}>{shown.detail}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <div role="status">{shown && !isProblem(shown) && renderResult()}</div>
      <div role="alert">{shown && isProblem(shown) && renderResult()}</div>
    </>
  );
});

PaymentResultStatus.propTypes = {
  form: PropTypes.oneOf(['charge', 'refund']).isRequired
};

export default PaymentResultStatus;
